import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import './i18n';
import { MantineProvider, createTheme, localStorageColorSchemeManager } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { DatesProvider } from '@mantine/dates';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './utils/queryClient';
import { Notifications } from '@mantine/notifications';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import { generateColors } from '@mantine/colors-generator';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { captureInstallPrompt } from './utils/installPrompt';
import 'dayjs/locale/ru';
import 'dayjs/locale/en';

const colorSchemeManager = localStorageColorSchemeManager({ key: 'color-scheme' });

// Событие «можно установить» приходит один раз при загрузке — ловим до рендера.
captureInstallPrompt();
// Воркер нужен только для пушей; регистрируем после загрузки, чтобы не мешать старту.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .catch((e) => console.warn('[sw] register failed', e));
  });
}

function AppWithTheme() {
  const { branding } = useTenant();
  const { i18n } = useTranslation();

  // If tenant uses default teal (#12b886), use Mantine's built-in 'teal' palette.
  // Only generate custom palette when the color is actually customized.
  const isDefaultColor = branding.primaryColor === '#12b886';

  const theme = createTheme({
    primaryColor: isDefaultColor ? 'teal' : 'brand',
    colors: isDefaultColor ? {} : { brand: generateColors(branding.primaryColor) },
    defaultRadius: 'md',
  });

  // Sync dayjs (used by Mantine date inputs) with the active i18n language so
  // month names + weekday headers render in the right locale.
  const datesLocale = i18n.language?.startsWith('en') ? 'en' : 'ru';
  dayjs.locale(datesLocale);

  return (
    <MantineProvider
      theme={theme}
      defaultColorScheme="dark"
      colorSchemeManager={colorSchemeManager}
    >
      <DatesProvider settings={{ locale: datesLocale, firstDayOfWeek: 1 }}>
        <ModalsProvider>
          <Notifications style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 2000 }} />
          <App />
        </ModalsProvider>
      </DatesProvider>
    </MantineProvider>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <TenantProvider>
        <QueryClientProvider client={queryClient}>
          <AppWithTheme />
        </QueryClientProvider>
      </TenantProvider>
    </HelmetProvider>
  </React.StrictMode>,
);

reportWebVitals();
